import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import {
  Network,
  Activity,
  CheckCircle,
  Clock,
  AlertTriangle,
  Package,
  TrendingUp,
  PlayCircle,
  BarChart3,
  Calendar,
  RefreshCw
} from 'lucide-react';

export default function ExecutionDashboard() {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await psAPI.getExecutionDashboard();
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Failed to load execution dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </Layout>
    );
  }

  const data = dashboardData || {};

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Execution Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">Project execution and control overview</p>
          </div>
          <Button onClick={fetchDashboard} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="active-projects-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Projects</p>
                  <p className="text-2xl font-bold text-orange-600">{data.active_projects || 0}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <PlayCircle className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="networks-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Networks</p>
                  <p className="text-2xl font-bold text-orange-600">{data.active_networks || 0}</p>
                  <p className="text-xs text-gray-400">of {data.total_networks || 0} total</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <Network className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="activities-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Activities</p>
                  <p className="text-2xl font-bold text-emerald-600">{data.total_activities || 0}</p>
                  <p className="text-xs text-gray-400">{data.completed_activities || 0} completed</p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-full">
                  <Activity className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="overdue-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Overdue Activities</p>
                  <p className="text-2xl font-bold text-red-600">{data.overdue_activities || 0}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Second Row Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Confirmations Today</p>
                  <p className="text-2xl font-bold text-amber-600">{data.confirmations_today || 0}</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-full">
                  <CheckCircle className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Confirmations This Week</p>
                  <p className="text-2xl font-bold text-amber-600">{data.confirmations_this_week || 0}</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-full">
                  <Calendar className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Open Reservations</p>
                  <p className="text-2xl font-bold text-orange-600">{data.open_reservations || 0}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <Package className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Goods Issues Today</p>
                  <p className="text-2xl font-bold text-orange-600">{data.goods_issues_today || 0}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <TrendingUp className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Cost Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Cost Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Total Planned Cost</span>
                  <span className="font-semibold">${(data.total_planned_cost || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Total Actual Cost</span>
                  <span className="font-semibold">${(data.total_actual_cost || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Cost Variance</span>
                  <span className={`font-semibold ${(data.cost_variance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    ${(data.cost_variance || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Overdue Activities
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.overdue_activities_list && data.overdue_activities_list.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {data.overdue_activities_list.map((activity, idx) => (
                    <div key={activity.activity_id || activity.activity_name || `ov-${idx}`} className="flex justify-between items-center p-3 bg-red-50 rounded-lg border border-red-100">
                      <div>
                        <p className="font-medium text-sm">{activity.activity_id}</p>
                        <p className="text-xs text-gray-500">{activity.description}</p>
                      </div>
                      <Badge variant="destructive">
                        {activity.days_overdue || 0}d overdue
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
                  <p>No overdue activities</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate('/ps/execution/networks')} data-testid="manage-networks-btn">
                <Network className="w-4 h-4 mr-2" />
                Manage Networks
              </Button>
              <Button onClick={() => navigate('/ps/execution/activities')} variant="outline" data-testid="view-activities-btn">
                <Activity className="w-4 h-4 mr-2" />
                View Activities
              </Button>
              <Button onClick={() => navigate('/ps/execution/confirmations')} variant="outline" data-testid="post-confirmation-btn">
                <CheckCircle className="w-4 h-4 mr-2" />
                Post Confirmation
              </Button>
              <Button onClick={() => navigate('/ps/execution/materials')} variant="outline" data-testid="manage-materials-btn">
                <Package className="w-4 h-4 mr-2" />
                Material Reservations
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Confirmations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Recent Confirmations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.recent_confirmations && data.recent_confirmations.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Confirmation ID</th>
                      <th className="text-left p-2">Activity</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-right p-2">Work</th>
                      <th className="text-left p-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_confirmations.map((conf, idx) => (
                      <tr key={conf.confirmation_id || conf.confirmation_number || `cf-${idx}`} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">{conf.confirmation_id}</td>
                        <td className="p-2">{conf.activity_id}</td>
                        <td className="p-2">
                          <Badge variant="outline">{conf.confirmation_type}</Badge>
                        </td>
                        <td className="p-2 text-right">{conf.actual_work || 0}h</td>
                        <td className="p-2 text-gray-500">{conf.confirmation_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No recent confirmations</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
